import { useEffect, useState, useContext, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, NavLink } from "react-router-dom";

import PropTypes from "prop-types";

import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SidenavCollapse from "cxfcomponents/Sidenav/SidenavCollapse";

import SidenavRoot from "cxfcomponents/Sidenav/SidenavRoot";
import { PORTAL_HEADER_HEIGHT, SIDENAV_WIDTH } from "cxfcomponents/portalShell";
import { THEME_COLORS } from "constants/theme";
import cxf_logo from "assets/images/cxf_logo.png";
import cxfabCircleLogo from "assets/images/cxfab_circle.png";

import { useSoftUIController, setMiniSidenav } from "context";
import pkg from "../../../package.json";

import { useAuth0 } from "@auth0/auth0-react";

import "./style.css";
import ProtectedPage from "authentication/ProtectedPage";
import { TenantContext } from "context/AppProvider/TenantContext";
import { setCompanyLogo as setLogo } from "redux/actions/portal/settingsActionCreator";
import {
  ThemedMaximizeIcon,
  ThemedMinimizeIcon,
} from "assets/CXFIcons/Designer/Toolbox";

function Sidenav({ routes, brand, brandName, color, onMouseEnter, onMouseLeave, ...rest }) {
  const [openMenuKey, setOpenMenuKey] = useState(null);
  const [, setOpenNestedKey] = useState(null);
  const [, setCompanyLogo] = useState(null);
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const rDispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const flush = true;
  const itemName = pathname.split("/")?.slice(1)[1];
  const { isAuthenticated } = useAuth0();
  const { tenantData, tenantLogoKey } = useContext(TenantContext);
  const { themeMode } = useSelector((store) => store?.portal?.settings);

  const handleMiniSidenav = () => {
    setMiniSidenav(dispatch, !miniSidenav);
  };

  useEffect(() => {
    if (!miniSidenav) return;
    setOpenMenuKey(null);
    setOpenNestedKey(null);
  }, [miniSidenav]);

  useEffect(() => {
    if (miniSidenav) return;
    const parent = routes.find(({ collapse }) => collapse?.some(({ route }) => route === pathname));
    if (parent) setOpenMenuKey(parent.key);
  }, [pathname, miniSidenav, routes]);

  useEffect(() => {
    if (!tenantData?.logo_url && !tenantLogoKey) return;

    const logo = tenantData?.logo_url || tenantLogoKey || null;
    setCompanyLogo(logo);

    if (logo) {
      rDispatch(setLogo(logo));
    }
  }, [tenantData, tenantLogoKey, rDispatch]);

  const handleMenuClick = useCallback(
    (key) => {
      if (miniSidenav) {
        setOpenMenuKey(key);
        return;
      }

      setOpenMenuKey((prev) => (prev === key ? null : key));
      setOpenNestedKey(null);
    },
    [miniSidenav]
  );

  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route, isComponentDown }) => {
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              style={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                key={key}
                name={name}
                icon={icon}
                active={openMenuKey === key}
                isMinified={miniSidenav}
                themeMode={themeMode}
                flush={flush}
              />
            </Link>
          );
        } else if (noCollapse && route && isComponentDown === "active") {
          returnValue = (
            <ProtectedPage key={key} route={route}>
              <NavLink to={route} style={{ textDecoration: "none" }}>
            <SidenavCollapse
                key={key}
                name={name}
                icon={icon}
                active={openMenuKey === key}
                open={!miniSidenav && openMenuKey === key}
                isMinified={miniSidenav}
                onClick={() => handleMenuClick(key)}
                itemName={itemName}
                themeMode={themeMode}
                flush={flush}
                collapse={collapse && isComponentDown === "active" ? collapse : null}
              />
              </NavLink>
            </ProtectedPage>
          );
        } else if (isComponentDown === "active") {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={openMenuKey === key}
              open={!miniSidenav && openMenuKey === key}
              isMinified={miniSidenav}
              onClick={() => handleMenuClick(key)}
              itemName={itemName}
              themeMode={themeMode}
              flush={flush}
              collapse={collapse && isComponentDown === "active" ? collapse : null}
            />
          );
        }
      } else if (type === "title") {
        let ttl = "";

        if (title.length > 5 && miniSidenav) {
          ttl = title.slice(0, 5);
        } else {
          ttl = title;
        }

        returnValue = (
          <SoftTypography
            key={key}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.6}
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {ttl}
          </SoftTypography>
        );
      } else if (type === "divider" && isComponentDown === "active") {
        returnValue = <Divider key={key} />;
      }

      return returnValue;
    }
  );

  if (!isAuthenticated) {
    return <></>;
  }

  const miniToggle = (
    <SidenavCollapse
      key="miniSidenav"
      name={miniSidenav ? "Maximize" : "Minimize"}
      icon={miniSidenav ? <ThemedMaximizeIcon /> : <ThemedMinimizeIcon />}
      noCollapse
      onClick={handleMiniSidenav}
      isMinified={miniSidenav}
      active={false}
      themeMode={themeMode}
      flush={flush}
    />
  );

  return (
    <>
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, miniSidenav, flush }}
        className="sidenav-wrapper"
      >
        <SoftBox
          sx={{
            height: PORTAL_HEADER_HEIGHT,
            minHeight: PORTAL_HEADER_HEIGHT,
            display: "flex",
            alignItems: "center",
            px: miniSidenav ? 1.5 : 2,
          }}
        >
          <SoftBox
            component="img"
            src={miniSidenav ? cxfabCircleLogo : cxf_logo}
            alt="CXFabric"
            sx={{ width: miniSidenav ? 44 : 149, maxWidth: "100%", height: "auto" }}
          />
        </SoftBox>

        <List className="sidebar-routes-list" sx={{ mb: 0, mt: 0 }}>
          <SoftBox sx={{ display: { xs: "block", xl: "none" } }}>{miniToggle}</SoftBox>
          {renderRoutes}
        </List>

        {miniSidenav ? (
          <SoftTypography className="app-version" sx={{ mt: "auto", mb: 2, textAlign: "center" }}>
            v{pkg.version}
            <br />
            &copy; {new Date().getFullYear()}
          </SoftTypography>
        ) : (
          <SoftTypography className="app-version" sx={{ mt: "auto", mb: 2, textAlign: "center" }}>
            &copy; {new Date().getFullYear()} CXFabric | v{pkg.version}
          </SoftTypography>
        )}
      </SidenavRoot>
      {!miniSidenav && (
        <IconButton
          aria-label="Minimize sidebar"
          title="Minimize sidebar"
          onClick={handleMiniSidenav}
          sx={(theme) => ({
            display: { xs: "none", xl: "flex" },
            position: "fixed",
            zIndex: 1201,
            top: PORTAL_HEADER_HEIGHT + 8,
            left: SIDENAV_WIDTH - 16,
            width: 32,
            height: 32,
            padding: "6px",
            color: theme.palette.mode === "dark" ? "#aa8bfd" : "#7443ff",
            backgroundColor: theme.palette.mode === "dark" ? THEME_COLORS.DARK.NAVBAR_BG : THEME_COLORS.LIGHT.NAVBAR_BG,
            border: "1px solid rgba(140, 150, 170, 0.3)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.18)",
            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? THEME_COLORS.DARK.ACTIVE_NAVBAR_BG : "#f2f6f9" },
          })}
        >
          <ThemedMinimizeIcon />
        </IconButton>
      )}
    </>
  );
}

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
